import { useEffect, useId, useRef, useState } from 'react';
import { usePrevious } from '../../../../../_helpers/hooks/usePrevious';
import SaveButtonIcon from './SaveButtonIcon';
import Tooltip from '../../tooltip/Tooltip';
import {
	Brand,
	SaveButtonBaseProps,
	SaveButtonState,
} from '../SaveButtonProps';
import { getKeys } from '../../../../../_helpers/keyboard';
import {
	getAriaLabel,
	getTooltipLabel,
	isButtonVisible,
	isSavedState,
} from '../_helpers/saveButtonHelpers';
import { joinString } from '../../../../../_helpers/join-string';

import '../SaveButton.scss';
import { sendSaveButtonDataLinkEvent } from '../../_helpers/dataLinkEventHelper.ts';

/**
 * Base Save button component that handles the common functionality of the save button variants:
 * - displays button variants based on SaveButtonState (it doesn't handle the state itself)
 * - displays tooltips
 * - blocks tooltip after click (in CLICKED button states)
 * - and manage the hover and focus of the button and the parent.
 */
const SaveButtonBase: React.FC<SaveButtonBaseProps> = (props) => {
	const id = 'save-button-' + useId().replace(/:/g, '');
	const ref = useRef<HTMLDivElement>(null);
	const [focusVisible, setFocusVisible] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const [stayedHovered, setStayedHovered] = useState<boolean>(null);
	const prevButtonState = usePrevious<SaveButtonState>(props.buttonState);

	useEffect(() => {
		if (
			(prevButtonState === SaveButtonState.SAVE_CLICKED ||
				prevButtonState === SaveButtonState.UNSAVE_CLICKED) &&
			(focusVisible || isHovered)
		) {
			setStayedHovered(true);
		}
	}, [props.buttonState]);

	const onButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		props.onClick?.();
		const operation =
			prevButtonState === SaveButtonState.SAVE_CLICKED ||
			prevButtonState === SaveButtonState.SAVED
				? SaveButtonState.UNSAVED
				: SaveButtonState.SAVED;
		sendSaveButtonDataLinkEvent(props.saveDataLinkEvent, operation);
	};

	const onMouseEnter = () => {
		setIsHovered(true);
	};

	const onMouseLeave = () => {
		if (stayedHovered) {
			setStayedHovered(false);
		}
		setIsHovered(false);
	};

	const onKeyDown = (event: React.KeyboardEvent) => {
		setFocusVisible(true);

		const keys = getKeys(event);
		if (keys.isEnter || keys.isSpace) {
			event.stopPropagation();
		}
	};

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const onBlur = (event) => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (event.relatedTarget && ref.current.contains(event.relatedTarget)) {
			return;
		}
		if (stayedHovered) {
			setStayedHovered(false);
		}
		setFocusVisible(false);
	};

	const ariaLabel = getAriaLabel(props);
	const tooltipLabel = getTooltipLabel(props, stayedHovered);
	const isVisible = isButtonVisible(props);

	return (
		<div
			title=""
			className={joinString([
				'blk-ac-save-button',
				props.className,
				isVisible ? '' : 'not-visible',
				props.brand === Brand.ISHARES ? 'ishares' : 'blackrock',
			])}
			ref={ref}
		>
			<button
				data-testid="save-button"
				className={joinString([
					props.buttonState,
					stayedHovered ? 'stayed hovered' : '',
				])}
				tabIndex={0}
				aria-label={ariaLabel}
				aria-pressed={isSavedState(props.buttonState)}
				onClick={onButtonClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onKeyDown={onKeyDown}
				onBlur={onBlur}
				data-tooltip-id={id}
				data-tooltip-content={tooltipLabel}
				data-tooltip-offset={9}
			>
				<SaveButtonIcon />
			</button>
			{!props.tooltipDisabled && <Tooltip id={id} />}
		</div>
	);
};

export default SaveButtonBase;
